import { create } from "zustand";
import { persist } from "zustand/middleware";
import { getCachedWorkers } from "./atelierStore";
import { disponibilitesApi } from "@services/api/disponibilites";

/* ── Types ── */

export type AvailabilityBlockType = "off" | "conge" | "rdv" | "autre";
export type ZoneType = "mecanique" | "carrosserie" | "service_rapide" | "pause" | "conge" | "peinture" | "diagnostique" | "campagne";

export interface AvailabilityBlock {
  id: string;
  workerId: string;
  label: string;
  type: AvailabilityBlockType;
  /** YYYY-MM-DD */
  date: string;
  /** Decimal hour, e.g. 8.25 = 08h15 */
  startHour: number;
  /** Duration in hours */
  durationHours: number;
  /** If true this block repeats every week */
  recurring: boolean;
  /** Day of week 0=Mon … 6=Sun (used for recurring) */
  dayOfWeek?: number;
  /** Zone type for unified atelier dispo */
  zoneType?: ZoneType;
}

export interface RecurringSummary {
  label: string;
  totalHours: number;
}

export interface CongeSummary {
  label: string;
  totalHours: number;
}

/* ── Store ── */

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

interface DisponibiliteState {
  selectedDate: string;
  viewMode: "jour" | "semaine";
  selectedWorkerId: string;
  selectedWorkerIds: string[];
  blocks: AvailabilityBlock[];
  searchQuery: string;
  isLoading: boolean;
  _fetchedOrgId: string | undefined;

  fetchBlocks: (orgId: string) => Promise<void>;
  setSelectedDate: (date: string) => void;
  setViewMode: (mode: "jour" | "semaine") => void;
  setSelectedWorkerId: (id: string) => void;
  setSearchQuery: (query: string) => void;
  toggleWorkerSelection: (id: string) => void;
  selectAllWorkers: () => void;
  deselectAllWorkers: () => void;

  addBlock: (block: Omit<AvailabilityBlock, "id">, orgId: string) => void;
  updateBlock: (
    id: string,
    updates: Partial<Omit<AvailabilityBlock, "id">>,
  ) => void;
  removeBlock: (id: string) => void;

  getBlocksForWorkerWeek: (
    workerId: string,
    weekStart: string,
  ) => AvailabilityBlock[];
  getRecurringSummary: (workerId: string) => RecurringSummary[];
  getCongesSummary: (workerId: string) => CongeSummary[];
}

/** Monday of the ISO week containing `dateStr` (YYYY-MM-DD) */
export function getMonday(dateStr: string): Date {
  const d = new Date(dateStr + "T00:00:00");
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  return monday;
}

/** Format a Date to YYYY-MM-DD */
export function toISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Get array of 7 dates (Mon-Sun) starting from a given monday */
export function getWeekDates(monday: Date): string[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return toISO(d);
  });
}

export const useDisponibiliteStore = create<DisponibiliteState>()(
  persist(
    (set, get) => ({
  selectedDate: todayIso(),
  viewMode: "semaine",
  selectedWorkerId: "",
  selectedWorkerIds: [],
  blocks: [],
  searchQuery: "",
  isLoading: false,
  _fetchedOrgId: undefined,

  fetchBlocks: async (orgId: string) => {
    const state = get();
    if (state.isLoading || state._fetchedOrgId === orgId) return;
    set({ isLoading: true, _fetchedOrgId: orgId });
    try {
      const blocks = await disponibilitesApi.list({ org_id: orgId, category: "disponibilite" });
      const workers = getCachedWorkers();
      set({
        blocks,
        isLoading: false,
        selectedWorkerIds: workers.map((w) => w.id),
        selectedWorkerId: workers[0]?.id || "",
      });
    } catch (error) {
      console.error("Error fetching disponibilites:", error);
      set({ blocks: [], isLoading: false, _fetchedOrgId: undefined });
    }
  },

  setSelectedDate: (date) => set({ selectedDate: date }),
  setViewMode: (mode) => set({ viewMode: mode }),
  setSelectedWorkerId: (id) => set({ selectedWorkerId: id }),
  setSearchQuery: (query) => set({ searchQuery: query }),
  toggleWorkerSelection: (id) =>
    set((state) => ({
      selectedWorkerIds: state.selectedWorkerIds.includes(id)
        ? state.selectedWorkerIds.filter((wId) => wId !== id)
        : [...state.selectedWorkerIds, id],
    })),
  selectAllWorkers: () =>
    set({ selectedWorkerIds: getCachedWorkers().map((w) => w.id) }),
  deselectAllWorkers: () => set({ selectedWorkerIds: [] }),

  addBlock: (block, orgId) => {
    if (!block.workerId) return;
    // Optimistic add with temp id
    const tempId = `dispo_${Date.now()}`;
    set((state) => ({
      blocks: [...state.blocks, { ...block, id: tempId }],
    }));
    const worker = getCachedWorkers().find((w) => w.id === block.workerId);
    disponibilitesApi.create(block, orgId, "disponibilite", worker?.name || "").then((created) => {
      set((state) => ({
        blocks: state.blocks.map((b) => (b.id === tempId ? created : b)),
      }));
    }).catch((e) => console.error("Error creating block:", e));
  },

  updateBlock: (id, updates) => {
    set((state) => ({
      blocks: state.blocks.map((b) => (b.id === id ? { ...b, ...updates } : b)),
    }));
    disponibilitesApi.update(id, updates).catch((e) => console.error("Error updating block:", e));
  },

  removeBlock: (id) => {
    set((state) => ({
      blocks: state.blocks.filter((b) => b.id !== id),
    }));
    disponibilitesApi.delete(id).catch((e) => console.error("Error deleting block:", e));
  },

  getBlocksForWorkerWeek: (workerId, weekStart) => {
    const { blocks, searchQuery } = get();
    const monday = getMonday(weekStart);
    const weekDates = getWeekDates(monday);

    return blocks.filter((b) => {
      if (b.workerId !== workerId) return false;
      if (
        searchQuery &&
        !b.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
        return false;

      // Direct date match
      if (weekDates.includes(b.date)) return true;

      // Recurring: check if dayOfWeek matches any day in the week
      if (b.recurring && b.dayOfWeek !== undefined) return true;

      return false;
    });
  },

  getRecurringSummary: (workerId) => {
    const { blocks } = get();
    return blocks
      .filter((b) => b.workerId === workerId && b.recurring)
      .map((b) => ({ label: b.label, totalHours: b.durationHours }));
  },

  getCongesSummary: (workerId) => {
    const { blocks } = get();
    return blocks
      .filter((b) => b.workerId === workerId && b.type === "conge")
      .map((b) => ({ label: b.label, totalHours: b.durationHours }));
  },
    }),
    {
      name: "disponibilite-prefs",
      partialize: (state) => ({
        viewMode: state.viewMode,
      }),
    },
  ),
);
